import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiError } from "@/lib/setupValues";
import { toast } from "sonner";
import { Edit2, Save, X } from "lucide-react";

function EditPanel({ catalogue, technology, onClose, onSaved }) {
  const [form, setForm] = useState({
    thickness: technology.thickness,
    gas: technology.gas,
    slowCut: technology.slowCut,
    mediumCut: technology.mediumCut,
    fastCut: technology.fastCut,
    pierceTimeSeconds: technology.pierceTimeSeconds,
    authoritative: technology.authoritative !== false,
  });
  const [saving, setSaving] = useState(false);
  const field = (key, label, step = "0.01") => (
    <label className="text-xs font-medium text-slate-700">
      {label}
      <input
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 numeric"
        type="number"
        min={key === "pierceTimeSeconds" ? "0" : "0.0001"}
        step={step}
        value={form[key]}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })}
      />
    </label>
  );
  const save = async () => {
    const payload = {
      ...form,
      thickness: Number(form.thickness),
      slowCut: Number(form.slowCut),
      mediumCut: Number(form.mediumCut),
      fastCut: Number(form.fastCut),
      pierceTimeSeconds: Number(form.pierceTimeSeconds),
      version: catalogue.version || 0,
    };
    if (![payload.thickness, payload.slowCut, payload.mediumCut, payload.fastCut].every((value) => value > 0)
        || payload.pierceTimeSeconds < 0) {
      toast.error("Thickness and all cut speeds must be positive; pierce time cannot be negative.");
      return;
    }
    setSaving(true);
    try {
      const path = "/laser-technology-catalogs/"
        + encodeURIComponent(catalogue.v1_machine_key)
        + "/technologies/"
        + encodeURIComponent(technology.id);
      const { data } = await api.patch(path, payload);
      onSaved(data.technology, data.version);
      toast.success("Laser technology updated");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mb-4 rounded-xl border-2 border-slate-900 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Editing machine cutting data</div>
          <div className="mt-1 font-semibold text-slate-900">{technology.description || technology.id}</div>
          <div className="mt-0.5 font-mono text-[10px] text-slate-500">{technology.id}</div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {field("thickness", "Thickness (mm)")}
        <label className="text-xs font-medium text-slate-700">
          Gas
          <select className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5" value={form.gas}
            onChange={(event) => setForm({ ...form, gas: event.target.value })}>
            {["Oxygen", "Nitrogen", "Air", "Mixed"].map((gas) => <option key={gas}>{gas}</option>)}
          </select>
        </label>
        {field("slowCut", "Small path mm/min", "1")}
        {field("mediumCut", "Medium path mm/min", "1")}
        {field("fastCut", "Large path mm/min", "1")}
        {field("pierceTimeSeconds", "Pierce seconds")}
      </div>
      <label className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-slate-700">
        <input type="checkbox" checked={form.authoritative}
          onChange={(event) => setForm({ ...form, authoritative: event.target.checked })} />
        Available to the selector
      </label>
      <div className="mt-4">
        <button type="button" disabled={saving} onClick={save}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving..." : "Save technology"}
        </button>
      </div>
    </div>
  );
}

export default function ImportedTechnologyCatalog() {
  const { session } = useAuth();
  const isAdmin = session?.user?.role === "admin";
  const [data, setData] = useState(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/laser-technology-catalogs")
      .then(({ data: next }) => {
        setData(next);
        setSelectedKey((current) => current || next.catalogues?.[0]?.v1_machine_key || "");
      })
      .catch((reason) => setError(apiError(reason)));
  }, [isAdmin]);

  const catalogue = data?.catalogues?.find((item) => item.v1_machine_key === selectedKey);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const source = catalogue?.technologies || [];
    if (!needle) return source;
    return source.filter((row) => [
      row.description, row.id, row.sourceTechRef, row.materialKey, row.gas,
      ...(row.sourceMaterials || []),
    ].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [catalogue, query]);

  if (!isAdmin) return null;
  if (error) return <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>;
  if (!data) return <div className="mb-6 text-sm text-slate-500">Loading machine cutting data...</div>;
  if (!data.catalogues?.length) return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      No machine cutting data has been loaded yet.
    </div>
  );

  const saveRow = (nextRow, version) => {
    setData((current) => ({
      ...current,
      catalogues: current.catalogues.map((item) => item.v1_machine_key !== selectedKey ? item : {
        ...item,
        version,
        technologies: item.technologies.map((row) => row.id === nextRow.id ? nextRow : row),
      }),
    }));
    setEditing(null);
  };

  return (
    <section className="mb-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="v1-technology-catalog">
      <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Machine cutting data</div>
            <h2 className="mt-1 text-lg font-semibold text-slate-950">Machine technology catalogue</h2>
            
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="mb-4 grid gap-3 md:grid-cols-[minmax(280px,1fr)_minmax(240px,1fr)_auto]">
          <label className="text-xs font-medium text-slate-700">
            Physical laser
            <select className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={selectedKey} onChange={(event) => { setSelectedKey(event.target.value); setEditing(null); }}>
              {data.catalogues.map((item) => (
                <option key={item.v1_machine_key} value={item.v1_machine_key}>
                  {item.machine_name} - {item.machine_power_kw} kW - {item.technology_count} rows
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-slate-700">
            Search mapping, gas or source
            <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. HA250, Oxygen, 60200120" />
          </label>
          <div className="self-end rounded-md bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
            {rows.length} / {catalogue?.technology_count || 0} rows
          </div>
        </div>

        {editing && <EditPanel catalogue={catalogue} technology={editing} onClose={() => setEditing(null)} onSaved={saveRow} />}

        <div className="max-h-[560px] overflow-auto rounded-xl border border-slate-200">
          <table className="min-w-[1120px] w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-slate-100 text-[10px] uppercase tracking-[0.1em] text-slate-600">
              <tr>
                <th className="px-3 py-2">Technology</th>
                <th className="px-3 py-2">Mapping</th>
                <th className="px-3 py-2">Thickness</th>
                <th className="px-3 py-2">Gas</th>
                <th className="px-3 py-2">Small / medium / large mm/min</th>
                <th className="px-3 py-2">Pierce</th>
                <th className="px-3 py-2">Use count</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="max-w-[270px] font-medium text-slate-900">{row.description || row.id}</div>
                    <div className="mt-0.5 font-mono text-[9px] text-slate-400">{row.sourceRef || row.id}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{row.materialKey}</div>
                    <div className="mt-0.5 max-w-[240px] text-[10px] leading-4 text-slate-500">{(row.sourceMaterials || []).join(", ") || "Family mapping"}</div>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{Number(row.thickness).toLocaleString()} mm</td>
                  <td className="px-3 py-2">{row.gas}</td>
                  <td className="px-3 py-2 tabular-nums">{Number(row.slowCut).toLocaleString()} / {Number(row.mediumCut).toLocaleString()} / {Number(row.fastCut).toLocaleString()}</td>
                  <td className="px-3 py-2 tabular-nums">{Number(row.pierceTimeSeconds || 0).toLocaleString()} s</td>
                  <td className="px-3 py-2 tabular-nums">{Number(row.timesUsed || 0).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => setEditing(row)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[10px] font-semibold hover:bg-white">
                      <Edit2 className="h-3 w-3" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No technologies match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
        
      </div>
    </section>
  );
}
